import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Icon, colors } from '@iq/react-components';
import { useDropzone } from 'react-dropzone';
import { RRule } from 'rrule';
import { NotFoundError } from '@iq/services';
import { pad } from '@rjsf/utils';
import { toZonedTime, getTimezoneOffset } from 'date-fns-tz';

import { getTags } from '../../bundles/tags';
import { getStaticComponents } from '../../bundles/components';
import { getHasPermission } from '../../bundles/auth';
import { getLimitedUsers, getLimitedUsersLoading, requestLimitedUsers } from '../../bundles/ad';
import { deleteUserEvent, getSeriesEvents, excludeUserEventOccurrence } from '../../bundles/events';
import {
  checkIsOnline,
  displayNotification,
  getTemporaryId,
  getNotification,
} from '../../bundles/notifications';
import { getTimezone } from '../../bundles/application';
import { FORM_STATES, STATELESS_EVENTS } from '../../constants';
import services from '../../services';
import config from '../../config';
import { getSharedCacheKey, useAsync, useMemoizedDeepComparison } from '../../utils';
import { utcToSite, siteToUtc } from '../../datetimeUtils';
import JsonEditor from '../JsonEditor';
import { DropdownMenu } from '../DropdownMenu';
import { FilesPanel } from '../panels/FilesPanel';
import SimpleModal from '../SimpleModal/SimpleModal';
import Indicator from '../panels/EventTimelinePanel/Indicator';
import TagSelect from '../TagSelect';
import ConfirmationDialog from '../ConfirmationDialog';

import {
  getEventMainSchema,
  getEventGeneralSchema,
  EventGeneralUiSchema,
  getEventPlanningSchema,
  EventPlanningUiSchema,
  getEventExecutionSchema,
  EventExecutionUiSchema,
  getEventAttributeSchemas,
} from './eventSchemas';
import CommentForm from './CommentForm';
import Tooltip from '../Tooltip/Tooltip';

export const getRelativeDate = (relative, date) => {
  const period = ['day', 'week', 'month'].find((p) => relative?.includes(p));
  if (period) {
    const d = new Date(date);
    const numberPeriods = Number.parseInt(relative.split('_')[0], 10);
    switch (period) {
      case 'month': {
        d.setMonth(d.getMonth() - numberPeriods);
        break;
      }
      case 'week': {
        d.setDate(d.getDate() - numberPeriods * 7);
        break;
      }
      // case 'day':
      default: {
        d.setDate(d.getDate() - numberPeriods);
        break;
      }
    }
    return d;
  }
  return null;
};

const formDataSections = {
  main: ['type', 'planned', 'state', 'assignedTo'],
  general: ['name', 'description', 'components', 'events'],
  planning: ['from', 'to', 'notifyAt', 'recurrenceRule'],
  execution: ['executionFrom', 'executionTo'],
  attachments: ['files'],
  // we don't include attributes as a section since it already takes the form of one (JSONB)
};
const dateSubSections = {
  planningDates: ['from', 'to'],
  executionDates: ['executionFrom', 'executionTo'],
};
const boolToStringFields = new Set(['planned']);
export const toFormData = (event) => {
  const data = Object.entries(event).reduce(
    (acc, [key, value]) => {
      const [sectionKey] =
        Object.entries(formDataSections).find((section) => section[1].includes(key)) || [];
      if (sectionKey) {
        const [dateSubSectionKey] =
          Object.entries(dateSubSections).find((section) => section[1].includes(key)) || [];
        if (dateSubSectionKey) {
          const isFrom = key.toLowerCase().includes('from');
          acc[sectionKey] = {
            ...acc[sectionKey],
            [dateSubSectionKey]: {
              ...acc[sectionKey][dateSubSectionKey],
              [isFrom ? 'from' : 'to']: value,
            },
          };
        } else {
          acc[sectionKey] = {
            ...acc[sectionKey],
            [key]: boolToStringFields.has(key) ? Boolean(value).toString() : value,
          };
        }
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {
      main: { type: 'general' },
      general: {},
      planning: { planningDates: { from: null, to: null }, recurrenceRule: null },
      execution: { executionDates: { from: null, to: null } },
      attributes: {},
      attachments: {},
    }
  );
  if (data.planning.planningDates.from === data.planning.planningDates.to) {
    data.planning.planningDates.to = null;
  }
  return data;
};

export const fromFormData = (event) => ({
  type: event.main.type,
  planned: event.main.planned === 'true',
  state: event.main.state,
  assignedTo: event.main.assignedTo === 'NA' ? null : event.main.assignedTo,
  name: event.general.name,
  description: event.general.description,
  components: event.general.components,
  events: event.general.events,
  from: event.planning.planningDates.from,
  to: event.planning.planningDates.to || event.planning.planningDates.from,
  recurrenceRule: event.planning.recurrenceRule,
  notifyAt: event.planning.notifyAt,
  executionFrom: event.execution.executionDates.from,
  executionTo: event.execution.executionDates.to,
  attributes: event.attributes,
  task: event.task,
  activeSeries: event.activeSeries,
  seriesId: event.seriesId,
});

export const fileName = (message) => {
  const firstQuoteIndex = message.indexOf("'");
  const lastQuoteIndex = message.lastIndexOf("'");
  return message.slice(firstQuoteIndex + 1, lastQuoteIndex);
};

const UserEventModal = (props) => {
  const {
    site,
    event,
    dateDomain,
    userEventTypes,
    hasHistory,
    hideInactiveSystemEvents = true,
    eventListColumns,
    initialEdit = false,
    componentScope,
    onBack,
    onNext,
    onClose,
  } = props;

  const dispatch = useDispatch();

  const editing = event && initialEdit ? FORM_STATES.edit : null;
  const memoizedEvent = useMemoizedDeepComparison(event);
  const initialFormData = useMemo(() => toFormData(memoizedEvent || {}), [memoizedEvent]);
  const initialEventState = event?.state;
  const [eventData, setEventData] = useState(initialFormData);
  const [eventTags, setEventTags] = useState(event?.tags || []);
  const [modalState, setModalState] = useState(
    editing || (event ? FORM_STATES.view : FORM_STATES.create)
  );

  const [initialComponents] = useState(event?.components || []);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [dropFiles, setDropFiles] = useState([]);
  const [showFileOptions, setShowFileOptions] = useState(false);
  const [siteFiles, setSiteFiles] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [showFileSelector, setShowFileSelector] = useState(false);
  const [filesToRemoveContext, setFilesToRemoveContext] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const isTask = !!eventData.task;
  const timezone = useSelector(getTimezone);
  const seriesEvents = useSelector(getSeriesEvents);
  const tags = useSelector(getTags);
  const staticComponents = useSelector(getStaticComponents);
  const siteUsers = useSelector(getLimitedUsers);
  const loadingLimitedUsers = useSelector(getLimitedUsersLoading);
  const siteUsersLoaded = !config.onPremise && !loadingLimitedUsers;

  useEffect(() => {
    if (!config.onPremise && site.id) {
      dispatch(requestLimitedUsers(site.id));
    }
  }, [dispatch, site.id]);
  const canEditLimited = useSelector((state) =>
    getHasPermission(state, 'events/WriteLimited', { org: site.org, site: site.id })
  );
  const canEditFull = useSelector((state) =>
    getHasPermission(state, 'events/Write', { org: site.org, site: site.id })
  );
  const canDelete = useSelector((state) =>
    getHasPermission(state, 'events/Delete', { org: site.org, site: site.id })
  );
  const canCreateTags = useSelector((state) =>
    getHasPermission(state, 'tags/Write', { org: site.org, site: site.id })
  );

  const sortedComponents = useMemo(() => {
    const sorted = staticComponents.toSorted((a, b) =>
      a.itemDesignation.localeCompare(b.itemDesignation)
    );

    if (
      componentScope?.activeComponentTypeFilter &&
      componentScope.activeComponentTypeFilter !== 'any'
    ) {
      return sorted.filter(
        (component) => component.type === componentScope.activeComponentTypeFilter
      );
    }

    return sorted;
  }, [staticComponents, componentScope]);

  // Filter to show only in-scope components from current selection
  const visibleComponents = useMemo(() => {
    const scopedIds = new Set(sortedComponents.map((c) => c.id));
    return (eventData.general.components || []).filter((id) => scopedIds.has(id));
  }, [eventData.general.components, sortedComponents]);

  const getMergedComponents = useCallback(
    (newComponents) => {
      const scopedIds = new Set(sortedComponents.map((c) => c.id));
      const outOfScopeComponents = initialComponents.filter((id) => !scopedIds.has(id));
      return [...new Set([...outOfScopeComponents, ...(newComponents || [])])];
    },
    [sortedComponents, initialComponents]
  );

  const mainReference = useRef();
  const generalReference = useRef();
  const planningReference = useRef();
  const executionReference = useRef();

  const isActiveSeries = !!event?.activeSeries && !!event?.recurrenceRule;
  const isActiveSeriesEvent = !!event?.activeSeries && !!event?.seriesId;
  const isUnsavedSeriesEvent = isActiveSeriesEvent && !event?.createdAt;
  const isSavedSeriesEvent = isActiveSeriesEvent && event?.createdAt;

  const onDrop = useCallback((dFiles) => setDropFiles((oldFiles) => [...oldFiles, ...dFiles]), []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    disabled: isSaving,
    noClick: true,
    onDrop,
  });

  const fileIds = eventData?.attachments?.files || [];
  const { response: files = [] } = useAsync(
    async (cache) => {
      if (fileIds.length > 0) {
        return Promise.all(
          fileIds.map((fileId) =>
            cache(
              () =>
                services.file.getFile(fileId).then(
                  (response) => response,
                  (error) => {
                    if (!(error instanceof NotFoundError)) {
                      console.error('Unable to fetch event file:', error);
                      dispatch(checkIsOnline());
                      dispatch(displayNotification(getNotification('getEventFile', 'error')()));
                    }
                    return { error };
                  }
                ),
              getSharedCacheKey('file/getFile', fileId)
            )
          )
        );
      }
      return [];
    },
    [JSON.stringify(fileIds)]
  );
  const foundFiles = useMemo(() => (files || []).filter((f) => !f.error), [files]);

  if (event?.id && event.id !== eventData.id) {
    setEventData(toFormData(event));
  }

  useEffect(() => {
    setAttachedFiles(foundFiles);
  }, [foundFiles]);

  const onUploadFiles = useCallback(() => {
    setShowFileOptions(false);
    open();
  }, [open]);

  useEffect(() => {
    const element = document.querySelector('.simple-modal-container');
    if (element) element.scrollTop = 0;
  }, [isTask]);

  const onSelectSiteFiles = () => {
    setShowFileOptions(false);
    setShowFileSelector(true);
  };

  const onAttachSiteFiles = useCallback(
    (sFiles = []) => {
      setSiteFiles((oldFiles) => [
        ...oldFiles,
        ...sFiles.filter(
          (sFile) =>
            !attachedFiles.map(({ id }) => id).includes(sFile.id) &&
            !oldFiles.map(({ id }) => id).includes(sFile.id)
        ),
      ]);
      setShowFileSelector(false);
    },
    [attachedFiles]
  );

  const onDeleteFile = useCallback(
    (file) => {
      if (modalState === 'create' || (modalState === 'edit' && !file.id)) {
        setDropFiles((oldFiles) => oldFiles.filter((item) => item !== file));
      }
      if (modalState === 'edit' && file.id) {
        setAttachedFiles((oldFiles) => oldFiles.filter((item) => item.id !== file.id));
        const singleContext =
          file.context && (file.context.identifier || file.context.length === 1);
        if (singleContext && file.context.identifier === event.id) {
          setFilesToDelete((queuedFiles) => [...queuedFiles, file]);
        }
        if (
          file.context &&
          file.context.length > 1 &&
          file.context.map((context) => context.identifier).includes(event?.id)
        ) {
          setFilesToRemoveContext((queuedFiles) => [...queuedFiles, file]);
        }
      }
    },
    [event?.id, modalState]
  );

  const onRemoveSiteFile = useCallback(
    (file) => setSiteFiles((oldFiles) => oldFiles.filter((f) => f.id !== file.id)),
    []
  );

  const { planning: { recurrenceRule } = {}, main: { state } = {} } = eventData || {};
  const stateOptions = useMemo(() => {
    const pendingOpt = { const: 'pending', title: 'Pending' };
    const todoOpt = { const: 'todo', title: 'To-do' };
    const openOpt = { const: 'open', title: 'Open' };
    const overdueOpt = { const: 'overdue', title: 'Overdue' };
    const closedOpt = { const: 'closed', title: 'Closed' };

    if ((recurrenceRule && recurrenceRule !== 'no-repeat') || modalState === FORM_STATES.create) {
      return [pendingOpt];
    }
    if (state === 'todo') {
      return [pendingOpt, todoOpt, openOpt, closedOpt];
    }
    if (state === 'overdue') {
      return [pendingOpt, openOpt, overdueOpt, closedOpt];
    }
    return [pendingOpt, openOpt, closedOpt];
  }, [recurrenceRule, state, modalState]);

  const schemaConfig = useMemo(
    () => ({
      site,
      dateDomain,
      modalState,
      components: sortedComponents,
      siteUsers: siteUsers || [],
      siteUsersLoaded,
      userEventTypes,
      hideInactiveSystemEvents,
      eventListColumns,
      stateOptions,
      isTask,
      sourceEventId: event?.id,
      isActiveSeriesEvent,
      onNext,
      timezone,
    }),
    [
      site,
      dateDomain,
      modalState,
      sortedComponents,
      siteUsers,
      siteUsersLoaded,
      userEventTypes,
      hideInactiveSystemEvents,
      eventListColumns,
      stateOptions,
      isTask,
      event?.id,
      isActiveSeriesEvent,
      onNext,
      timezone,
    ]
  );
  const mainSchema = useMemo(() => getEventMainSchema(schemaConfig), [schemaConfig]);
  const mainFormData = useMemo(() => {
    const assignedTo = eventData.main.assignedTo;
    if (
      siteUsersLoaded &&
      assignedTo &&
      assignedTo !== 'NA' &&
      !siteUsers.some((user) => user.id === assignedTo)
    ) {
      return { ...eventData.main, assignedTo: 'NA' };
    }
    return eventData.main;
  }, [eventData.main, siteUsersLoaded, siteUsers]);
  const generalSchema = useMemo(() => getEventGeneralSchema(schemaConfig), [schemaConfig]);
  const planningSchema = useMemo(() => getEventPlanningSchema(schemaConfig), [schemaConfig]);
  const executionSchema = useMemo(() => getEventExecutionSchema(schemaConfig), [schemaConfig]);
  const attributeSchemas = useMemo(() => getEventAttributeSchemas(schemaConfig), [schemaConfig]);

  const editMenuOptions = useMemo(() => {
    const options = [];
    if (modalState === FORM_STATES.view && (canEditFull || canEditLimited)) {
      options.push({
        component: <div>Edit event</div>,
        onSelect: () =>
          setModalState(() => (canEditFull ? FORM_STATES.edit : FORM_STATES.editLimited)),
      });
    }
    if ((modalState === FORM_STATES.view || isActiveSeries) && canDelete) {
      options.push({
        component: <div>{`Delete ${isActiveSeries ? 'series' : 'event'}`}</div>,
        onSelect: () => setShowDeleteConfirm(true),
      });
    }
    return options;
  }, [modalState, canEditFull, canEditLimited, canDelete, isActiveSeries]);

  const onConfirmDeletion = useCallback(() => {
    setShowDeleteConfirm(false);
    const onDeleteConfirm = () => onClose({ refresh: true });
    if (event?.createdAt) {
      dispatch(deleteUserEvent(event?.id, onDeleteConfirm));
    } else {
      dispatch(excludeUserEventOccurrence(event, onDeleteConfirm));
    }
  }, [event, dispatch, onClose]);

  const confirmDeletionBody = useMemo(
    () =>
      isActiveSeries ? (
        <>
          <p>Are you sure you want to delete this event series?</p>
          <p>Unedited events from this series will no longer be available.</p>
        </>
      ) : (
        <>
          <p>Are you sure you want to delete this event?</p>
          <p>{`${config.onPremise ? 'This will remove the event and any associated comments' : 'This will remove the event and any associated files and comments'}`}</p>
        </>
      ),
    [isActiveSeries]
  );

  const deleteEventConfirmation = useMemo(
    () =>
      showDeleteConfirm ? (
        <ConfirmationDialog
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={onConfirmDeletion}
          confirmType="danger"
          confirmText="Delete"
          title={`Delete Event${isActiveSeries ? ' Series' : ''}`}
          body={confirmDeletionBody}
        />
      ) : null,
    [isActiveSeries, showDeleteConfirm, confirmDeletionBody, onConfirmDeletion]
  );

  const createFile = async (file) => {
    const formData = new FormData();
    for (const key of Object.keys(file)) {
      if (Array.isArray(file[key])) {
        for (const item of file[key]) {
          formData.append(`${key}[]`, item);
        }
      } else {
        formData.append(key, file[key]);
      }
    }
    try {
      return await services.file.createFile(formData);
    } catch (error) {
      return { id: null, error };
    }
  };

  const getUpdatedFiles = useCallback(
    async (eventId) => {
      let fileUpdateErrors = []; // Array to collect errors

      try {
        const newFiles = await Promise.all(
          dropFiles
            .map((file) => ({
              file,
              site: site.id,
              org: site.org,
              tags: eventTags || [],
              components: eventData.general.components || [],
              context: JSON.stringify([{ type: 'event', identifier: eventId }]),
            }))
            .map((fileData) => createFile(fileData))
        );

        // Filter out files with success and error
        const successfulNewFiles = newFiles.filter((file) => file.id !== null);
        const ErrorFiles = newFiles.filter((file) => file.id === null);

        if (ErrorFiles.length > 0) {
          // Collect error messages
          fileUpdateErrors = ErrorFiles.map((result) => result.error);
        }

        // only delete files that have a single, matching context
        await Promise.all(filesToDelete.map((file) => services.file.deleteFile(file.id)));
        // else we update to remove this context
        await Promise.all(
          filesToRemoveContext.map((file) =>
            services.file.updateFile(file.id, {
              ...file,
              context: file.context.filter((context) => context.identifier !== eventId),
            })
          )
        );
        // add event context to any site files attached
        await Promise.all(
          siteFiles.map((file) => {
            let newComps = [];
            let newTags = [];
            if (eventData?.general?.components) {
              newComps = eventData.general.components.filter(
                (c) => !(file.components || []).includes(c)
              );
            }
            if (tags) {
              newTags = eventTags.filter((t) => !(file.tags || []).includes(t));
            }
            return services.file.updateFile(file.id, {
              ...file,
              components: [...(file.components || []), ...newComps],
              tags: [...(file.tags || []), ...newTags],
              context: [file.context, { type: 'event', identifier: eventId }]
                .flat()
                .filter(Boolean),
            });
          })
        );

        return [
          ...attachedFiles.map((f) => f.id),
          ...successfulNewFiles.map((f) => f.id),
          ...siteFiles.map((f) => f.id),
        ];
      } catch (error) {
        console.error('Unable to update files:', error);
        dispatch(checkIsOnline());
        dispatch(displayNotification(getNotification('updateEventFiles', 'error')()));
        return [];
      } finally {
        if (Array.isArray(fileUpdateErrors)) {
          for (const error of fileUpdateErrors) {
            const temporaryId = getTemporaryId();
            const isDuplicateFile = error?.response?.status === 453;
            const existingFileName = isDuplicateFile ? fileName(error.response.data.message) : '';
            const errorMessage = isDuplicateFile ? error.response.data.message : error?.message;
            console.error('Unable to update files:', error);
            dispatch(checkIsOnline());
            dispatch(
              displayNotification(
                getNotification('createFile', 'error')(
                  errorMessage,
                  temporaryId,
                  !!existingFileName
                )
              )
            );
          }
        }
      }
    },
    [
      eventData.general.components,
      eventTags,
      dropFiles,
      filesToDelete,
      filesToRemoveContext,
      siteFiles,
      attachedFiles,
      site.id,
      site.org,
      tags,
      dispatch,
    ]
  );

  const onSubmitEvent = useCallback(
    async (submitEvent) => {
      let hasErrors = false;
      // first, validate forms
      const { errors: mainErrors } = mainReference.current?.validate(mainFormData) || {
        errors: [],
      };
      const { errors: generalErrors } = generalReference.current?.validate(eventData.general) || {
        errors: [],
      };
      const { errors: planningErrors } = planningReference.current?.validate(
        eventData.planning
      ) || {
        errors: [],
      };
      const { errors: executionErrors } = executionReference.current?.validate(
        eventData.execution
      ) || {
        errors: [],
      };
      if (mainErrors.length > 0) {
        hasErrors = true;
        mainReference.current?.onSubmit(submitEvent);
      }

      if (generalErrors.length > 0) {
        hasErrors = true;
        generalReference.current?.onSubmit(submitEvent);
      }

      if (planningErrors.length > 0) {
        hasErrors = true;
        planningReference.current?.onSubmit(submitEvent);
      }
      if (executionErrors.length > 0) {
        hasErrors = true;
        executionReference.current?.onSubmit(submitEvent);
      }

      if (!hasErrors) {
        setIsSaving(true);
        const eventPayload = fromFormData({ ...eventData, main: mainFormData });

        eventPayload.components = getMergedComponents(eventPayload.components);

        // add other fields
        eventPayload.site = site.id;
        eventPayload.org = site.org;
        eventPayload.tags = eventTags;
        eventPayload.notificationDate = getRelativeDate(eventPayload.notifyAt, eventPayload.from);

        // automatic event states
        if (eventPayload.notifyAt === 'none' && eventPayload.state === 'to-do') {
          eventPayload.state = 'pending';
        }

        // clear legacy recurrence rule
        const hasValidRecurrenceRule = eventPayload.recurrenceRule?.includes('RRULE:');
        if (hasValidRecurrenceRule) {
          // we need to include tz info and adjust dtstart to site time
          const siteStart = utcToSite(eventPayload.from, timezone);
          const time = `${pad(siteStart.getHours(), 2)}${pad(siteStart.getMinutes(), 2)}${pad(siteStart.getSeconds(), 2)}`;

          eventPayload.recurrenceRule = `DTSTART;TZID=${timezone}:${siteStart.getFullYear()}${pad(
            siteStart.getMonth() + 1,
            2
          )}${pad(siteStart.getDate(), 2)}T${time}\n${eventPayload.recurrenceRule.replace(
            ';BYMONTHDAY=null',
            ''
          )}`;
        } else {
          eventPayload.recurrenceRule = null;
        }

        try {
          if (modalState === FORM_STATES.create) {
            const { id: eventId } = await services.event.createEvent(eventPayload);
            await services.event.updateEvent(eventId, { files: await getUpdatedFiles(eventId) });
          }

          if (modalState === FORM_STATES.edit) {
            const eventFiles = await getUpdatedFiles(event.id);
            if (isUnsavedSeriesEvent) {
              // instantiating series event
              await services.event.createEvent({
                ...eventPayload,
                id: event.id,
                files: eventFiles,
              });
            } else {
              eventPayload.activeSeries = !!eventPayload.recurrenceRule;
              await services.event.updateEvent(event.id, { ...eventPayload, files: eventFiles });
            }
          }

          if (modalState === FORM_STATES.editLimited) {
            const eventFiles = await getUpdatedFiles(event.id);
            if (!isUnsavedSeriesEvent) {
              await services.event.updateLimitedEvent(event.id, {
                ...eventPayload,
                files: eventFiles,
              });
            }
          }

          dispatch(
            displayNotification(
              getNotification('saveEvent', 'success')(
                modalState.split('-')[0],
                event?.id || 'save-event',
                isActiveSeries ? 'series' : ''
              )
            )
          );
          const getSeries =
            modalState === FORM_STATES.create
              ? !!eventPayload.recurrenceRule
              : eventPayload.activeSeries && !!eventPayload.recurrenceRule;
          onClose({ getSeries, refresh: true });
          setIsSaving(false);
        } catch (submitError) {
          console.error(`Unable to ${modalState.replace('Limited', '')} event:`, submitError);
          dispatch(checkIsOnline());
          dispatch(
            displayNotification(
              getNotification('saveEvent', 'error')(
                modalState.split('-')[0],
                event?.id || 'save-event',
                isActiveSeries ? 'series' : ''
              )
            )
          );
          setIsSaving(false);
        }
      }
    },
    [
      eventData,
      mainFormData,
      site.id,
      site.org,
      timezone,
      modalState,
      eventTags,
      isUnsavedSeriesEvent,
      getUpdatedFiles,
      event?.id,
      isActiveSeries,
      onClose,
      dispatch,
      getMergedComponents,
    ]
  );

  const saveEvent = useCallback(async () => {
    // called from within CommentForm in the case that a comment is added to a future series event
    // as comments are only added in view mode, we don't need any validation, data transformation or file handling
    const eventPayload = fromFormData({ ...eventData, main: mainFormData });

    // add other fields
    eventPayload.site = site.id;
    eventPayload.org = site.org;
    eventPayload.id = event.id;
    try {
      const { id } = await services.event.createEvent(eventPayload);
      return !!id;
    } catch (submitError) {
      console.error(`Unable to save event:`, submitError);
      dispatch(checkIsOnline());
      dispatch(
        displayNotification(
          getNotification('saveEvent', 'error')(FORM_STATES.create, event?.id || 'save-event')
        )
      );
      return false;
    }
  }, [eventData, mainFormData, site.id, site.org, event?.id, dispatch]);

  const customMainValidator = useCallback(
    (data, errors) => {
      const { type, state } = data;

      const overdueStateError = 'planning date must be in the future for this status';
      if (
        !STATELESS_EVENTS.includes(type) &&
        initialEventState === 'overdue' &&
        state !== 'overdue' &&
        state !== 'closed' &&
        new Date(
          eventData?.planning?.planningDates?.to || eventData?.planning?.planningDates?.from
        ) < Date.now()
      ) {
        errors.state.addError(overdueStateError);
      }
      if (
        !STATELESS_EVENTS.includes(type) &&
        initialEventState === 'overdue' &&
        state !== 'overdue' &&
        new Date(
          eventData?.planning?.planningDates?.to || eventData?.planning?.planningDates?.from
        ) > Date.now()
      ) {
        errors.state.__errors = errors.state.__errors.filter(
          (error) => error !== overdueStateError
        );
      }

      const todoStateError = 'notify date must be in the future for this status';
      const notifyAt =
        eventData?.planning?.notifyAt &&
        eventData.planning.notifyAt !== 'none' &&
        eventData?.planning?.planningDates?.from
          ? new Date(
              getRelativeDate(eventData.planning.notifyAt, eventData.planning.planningDates.from)
            )
          : null;

      if (
        !STATELESS_EVENTS.includes(type) &&
        initialEventState === 'todo' &&
        state === 'pending' &&
        notifyAt &&
        notifyAt < Date.now()
      ) {
        errors.state.addError(todoStateError);
      }
      if (
        !STATELESS_EVENTS.includes(type) &&
        initialEventState === 'todo' &&
        state !== 'pending' &&
        notifyAt &&
        notifyAt > Date.now()
      ) {
        errors.state.__errors = errors.state.__errors.filter((error) => error !== todoStateError);
      }

      return errors;
    },
    [
      initialEventState,
      eventData?.planning?.planningDates?.to,
      eventData?.planning?.planningDates?.from,
      eventData?.planning?.notifyAt,
    ]
  );

  const customPlanningValidator = useCallback(
    (data, errors) => {
      const {
        planningDates: { from, to },
        recurrenceRule,
      } = data;

      const isRecurring = recurrenceRule?.includes('RRULE:');
      const startNotInFutureError = 'start date must be in the future for recurring events';
      if (isRecurring && from && toZonedTime(from, timezone).getTime() < Date.now()) {
        errors.planningDates.from.addError(startNotInFutureError);
      } else if (errors.planningDates.from.__errors.includes(startNotInFutureError)) {
        errors.planningDates.from.__errors = errors.planningDates.from.__errors.filter(
          (error) => error !== startNotInFutureError
        );
      }

      const startGtEndError = 'start date must preceed end date';
      if (from && to && to < from) {
        errors.planningDates.from.addError(startGtEndError);
      } else if (errors.planningDates.from.__errors.includes(startGtEndError)) {
        errors.planningDates.from.__errors = errors.planningDates.from.__errors.filter(
          (error) => error !== startGtEndError
        );
      }

      const noStartError = 'date is required';
      if (!from) {
        errors.planningDates.from.addError(noStartError);
      } else if (errors.planningDates.from.__errors.includes(noStartError)) {
        errors.planningDates.from.__errors = errors.planningDates.from.__errors.filter(
          (error) => error !== noStartError
        );
      }

      const startAfterUntilError = 'start date must fall before recurrence until date';
      if (recurrenceRule && recurrenceRule.indexOf('UNTIL') > 0) {
        const rRuleObject = RRule.fromString(recurrenceRule);
        const { until } = rRuleObject.origOptions;
        const siteTime = siteToUtc(until, timezone);
        const appTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
        const appTzOffset = getTimezoneOffset(appTimezone);
        const untilForCompare = new Date(siteTime.getTime() - appTzOffset);
        if (from && from > untilForCompare.toISOString()) {
          errors.planningDates.from.addError(startAfterUntilError);
        } else if (errors.planningDates.from.__errors.includes(startAfterUntilError)) {
          errors.planningDates.from.__errors = errors.planningDates.from.__errors.filter(
            (error) => error !== startAfterUntilError
          );
        }
      }
      if (recurrenceRule && recurrenceRule === 'invalid') {
        errors.recurrenceRule.addError(' ');
      } else {
        errors.recurrenceRule.__errors = [];
      }

      return errors;
    },
    [timezone]
  );

  const customExecutionValidator = useCallback((data, errors) => {
    const {
      executionDates: { from: executionFrom, to: executionTo },
    } = data;
    if (executionFrom && executionTo && executionTo < executionFrom) {
      errors.executionDates.addError('start date must preceed end date');
    }
    if (executionTo && !executionFrom) {
      errors.executionDates.addError('start date required if end is present');
    }
    return errors;
  }, []);

  const editable = modalState === FORM_STATES.edit || modalState === FORM_STATES.create;
  const editableLimited = editable || modalState === FORM_STATES.editLimited;

  const typedAttributesSchema = attributeSchemas[eventData?.main?.type];
  const shouldShowAttributes =
    Object.keys(typedAttributesSchema?.properties || []).length > 0 &&
    (modalState !== FORM_STATES.view || Object.keys(eventData?.attributes || {}).length > 0);

  const attributesForm = useMemo(
    () =>
      eventData?.main?.type ? (
        <JsonEditor
          schema={attributeSchemas[eventData.main.type]}
          formData={eventData.attributes}
          initialEditMode={true}
          showEditButton={false}
          showButtons={false}
          editorOnly
          onFormChange={({ formData }) =>
            setEventData((data) => ({ ...data, attributes: formData }))
          }
        />
      ) : null,
    [eventData?.main?.type, eventData?.attributes, attributeSchemas]
  );

  const buildFileList = useCallback(
    (filesToList, canEdit, onFileDelete) => (
      <FilesPanel
        hideUploadButton
        fileLimit={15}
        selector
        filesToList={filesToList}
        onFileDelete={(file) => onFileDelete(file)}
        editEvent={canEdit}
        isEventPage={true}
        site={site}
      />
    ),
    [site]
  );

  const attachmentsField = useMemo(
    () =>
      attachedFiles.length > 0 ||
      dropFiles.length > 0 ||
      siteFiles.length > 0 ||
      modalState !== FORM_STATES.view ? (
        <>
          <div className="event-modal__form-field-group">
            <div className="event-modal__label-row">
              <label>Attachments</label>
              {(editable || editableLimited) && (
                <Button
                  activity="secondary"
                  slim
                  onClick={() => setShowFileOptions(true)}
                  onBlur={(event) => {
                    if (!event?.relatedTarget?.classList.contains('file-options-element')) {
                      setShowFileOptions(false);
                    }
                  }}
                  type="button"
                >
                  Add
                </Button>
              )}
              <input {...getInputProps()} />
              {showFileOptions && (
                <div
                  className="event-modal__file-options file-options-element"
                  tabIndex={0}
                >
                  <Button
                    className="file-options-element"
                    onClick={onUploadFiles}
                    slim
                  >
                    Add files
                  </Button>

                  <Button
                    className="file-options-element"
                    onClick={onSelectSiteFiles}
                    slim
                  >
                    Select site files
                  </Button>
                </div>
              )}
            </div>
            <div className="event-modal__attachments">
              {attachedFiles.length > 0 && buildFileList(attachedFiles, editable, onDeleteFile)}
              {dropFiles.length > 0 && buildFileList(dropFiles, editable, onDeleteFile)}
              {siteFiles.length > 0 && buildFileList(siteFiles, editable, onRemoveSiteFile)}
            </div>
            {(editable || editableLimited) && (
              <div
                {...getRootProps()}
                className={`event-modal__file-input${
                  (attachedFiles.length === 0 &&
                    dropFiles.length === 0 &&
                    siteFiles.length === 0) ||
                  isDragActive
                    ? ' drop-zone-active'
                    : ''
                }`}
              >
                <div
                  className="drop-zone"
                  onClick={open}
                >
                  <input
                    name="files"
                    {...getInputProps()}
                  />
                  <p>Drop or click to upload</p>
                </div>
              </div>
            )}
          </div>
          {showFileSelector && (
            <SimpleModal
              className="file-selector-modal"
              onClose={() => setShowFileSelector(false)}
              title="Select files"
            >
              <FilesPanel
                hideUploadButton
                fileLimit={15}
                selector
                onAttach={onAttachSiteFiles}
                onClose={() => setShowFileSelector(false)}
                site={site}
              />
            </SimpleModal>
          )}
        </>
      ) : (
        <div className="event-modal__attachments--empty">No attachments found</div>
      ),
    [
      modalState,
      attachedFiles,
      dropFiles,
      siteFiles,
      showFileOptions,
      editable,
      editableLimited,
      onDeleteFile,
      showFileSelector,
      onRemoveSiteFile,
      buildFileList,
      getInputProps,
      getRootProps,
      isDragActive,
      onAttachSiteFiles,
      onUploadFiles,
      open,
      site,
    ]
  );

  const handleMainChange = useCallback(
    ({ formData }) => {
      const selectedEventType = userEventTypes.find((t) => t.id === formData.type);
      setEventData((data) => ({
        ...data,
        main: formData,
        planning: {
          ...data.planning,
          recurrenceRule: selectedEventType?.task ? data.planning.recurrenceRule : null,
        },
        task: !!selectedEventType?.task,
      }));
    },
    [userEventTypes]
  );

  const handlePlanningChange = useCallback(({ formData }) => {
    if (formData.notifyAt) {
      formData.notificationDate = Number.isNaN(Date.parse(formData.notifyAt))
        ? null
        : formData.notifyAt;
    }

    const hasValidRecurrenceRule = formData.recurrenceRule?.includes('RRULE:');
    setEventData((data) => ({
      ...data,
      planning: formData,
      main: hasValidRecurrenceRule ? { ...data.main, state: 'pending' } : data.main,
    }));
  }, []);
  const handleEditSeries = useCallback(() => {
    const seriesEvent = seriesEvents.find((event_) => event_.id === event?.seriesId);
    if (seriesEvent) {
      onNext({ eventType: 'user', event: seriesEvent, eventId: seriesEvent.id, edit: true });
    }
  }, [seriesEvents, event?.seriesId, onNext]);

  const isStateLessEvent = STATELESS_EVENTS.includes(event?.type);

  const getRecurrenceText = useCallback(
    (frequency, interval) => (
      <>
        Occurs <span className="recurrence-text">once</span> every{' '}
        <span className="recurrence-text">{`${interval || ''} ${frequency}`}</span>
      </>
    ),
    []
  );

  const RRuleToText = useCallback(() => {
    const dayMap = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    const seriesEvent = seriesEvents.find((event_) => event_.id === event?.seriesId);

    if (!seriesEvent?.recurrenceRule) return '';

    const rule = RRule.fromString(seriesEvent.recurrenceRule);
    const { freq, interval, byweekday } = rule.options;

    if (freq === RRule.WEEKLY) {
      const days = byweekday
        ?.toSorted((a, b) => a.weekday - b.weekday)
        ?.map((day) => dayMap[day.toString()]);
      const daysText =
        days.length > 1 ? `${days.slice(0, -1).join(', ')} and ${days.slice(-1)}` : days[0] || '';

      const intervalText = `${interval} Week${interval > 1 ? 's' : ''}`;
      return (
        <>
          Occurs every <span className="recurrence-text"> {daysText}</span> every{' '}
          <span className="recurrence-text">{intervalText}</span>
        </>
      );
    }

    if (freq === RRule.MONTHLY) {
      switch (interval) {
        case 12: {
          return getRecurrenceText('Year');
        }
        case 1: {
          return getRecurrenceText('Month');
        }
        default: {
          return getRecurrenceText('Months', interval);
        }
      }
    }

    if (freq === RRule.YEARLY) {
      return interval === 1 ? getRecurrenceText('Year') : getRecurrenceText('Years', interval);
    }
    return '';
  }, [seriesEvents, event?.seriesId, getRecurrenceText]);

  const SeriesEventSubtitle = useCallback(() => {
    if (isActiveSeriesEvent && canEditFull) {
      return (
        <div className="series-event-info">
          <div className="series-cta">
            <Icon icon="he-refresh" />
            <div>{RRuleToText()}</div>

            {isUnsavedSeriesEvent && (
              <span className="series-link">
                <Button
                  className="series-link-btn"
                  type="button"
                  design="text"
                  activity="primary"
                  onClick={handleEditSeries}
                >
                  Edit series
                </Button>
              </span>
            )}

            {isSavedSeriesEvent && (
              <>
                <div className="saved-series-info">
                  <Icon
                    icon="he-info"
                    style={{ color: colors.StatusBlue }}
                  />
                </div>
                <div className="saved-series-info--outer">
                  <div className="saved-series-info--inner">
                    <p>
                      Recurring event series can only be edited in unmodified events with a pending
                      status.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }

    if (modalState === FORM_STATES.edit && (isActiveSeries || isActiveSeriesEvent)) {
      return <div className="subtitle-edit">Editing a series event.</div>;
    }

    return null;
  }, [
    isActiveSeriesEvent,
    canEditFull,
    RRuleToText,
    isUnsavedSeriesEvent,
    handleEditSeries,
    isSavedSeriesEvent,
    modalState,
    isActiveSeries,
  ]);

  const userEventName = eventData?.general?.name || 'User Event';

  return (
    <>
      <SimpleModal
        size="l"
        onClose={onClose}
        scrollResetKey={eventData?.main?.type}
        className={`event-modal-wrapper ${modalState}`}
        headerLeft={
          hasHistory ? (
            <Button
              design="text"
              tooltip="Back"
              onClick={onBack}
            >
              <Icon icon="he-previous" />
            </Button>
          ) : null
        }
        title={
          <div className="event-modal-header">
            <Indicator
              eventState={isStateLessEvent ? 'stateless' : eventData?.main?.state}
              round={true}
              size={12}
            />
            <Tooltip
              content={userEventName}
              disabled={userEventName.length < 72}
              key={userEventName}
            >
              <div className="title">{userEventName}</div>
            </Tooltip>
            <TagSelect
              onChange={(selected) => setEventTags(selected)}
              value={eventTags}
              creatable={canCreateTags}
              popout
              tagLimit={3}
              availableTags={tags}
              editable={modalState === FORM_STATES.edit || modalState === FORM_STATES.create}
              menuMinWidth={20}
              menuMaxWidth={20}
              category="events"
            />
          </div>
        }
        subtitle={<SeriesEventSubtitle />}
        headerRight={
          <>
            {(canEditFull || (canEditLimited && !isUnsavedSeriesEvent)) &&
              event &&
              editMenuOptions.length > 0 &&
              (modalState === FORM_STATES.view || isActiveSeries) && (
                <DropdownMenu
                  trigger="he-moreoptionshorizontal"
                  triggerSize="s"
                  triggerThemeLightColor={colors.LightApplicationPrimaryText}
                  menuOptions={editMenuOptions}
                  className="event-modal-menu"
                  menuXPlacement="left"
                  styles={{
                    dropdownIndicator: { padding: '0 0.5rem', filter: 'none' },
                    option: { cursor: 'pointer' },
                  }}
                />
              )}
          </>
        }
      >
        <div className={`event-modal ${modalState}`}>
          <div className={`top-section`}>
            <JsonEditor
              formRef={mainReference}
              className="top-section__content"
              schema={mainSchema}
              formData={mainFormData}
              initialEditMode={true}
              showEditButton={false}
              showButtons={false}
              editorOnly
              onFormChange={handleMainChange}
              customValidate={customMainValidator}
            />
          </div>

          <div className={`body-section ${isTask ? 'show-planning' : ''}`}>
            <div className="event-modal__column">
              <div className="event-modal__group-header">General</div>
              <div className="general">
                <JsonEditor
                  formRef={generalReference}
                  schema={generalSchema}
                  formData={{ ...eventData.general, components: visibleComponents }}
                  uiSchema={EventGeneralUiSchema}
                  initialEditMode={true}
                  showEditButton={false}
                  showButtons={false}
                  editorOnly
                  onFormChange={({ formData }) => {
                    setEventData((data) => ({
                      ...data,
                      general: {
                        ...formData,
                        components: getMergedComponents(formData.components),
                      },
                    }));
                  }}
                />
              </div>
              {event && modalState === FORM_STATES.view && (
                <>
                  <div className="event-modal__group-header">Comments</div>
                  <CommentForm
                    site={site}
                    parentId={event.id}
                    parentFiles={foundFiles}
                    parentComponents={event.components}
                    isUnsavedSeriesEvent={isUnsavedSeriesEvent}
                    saveEvent={saveEvent}
                  />
                </>
              )}
            </div>

            <div className="event-modal__column">
              <div className="event-modal__group-header">{isTask ? 'Planning' : 'Time'}</div>
              <div className="planning">
                <JsonEditor
                  formRef={planningReference}
                  schema={planningSchema}
                  formData={eventData.planning}
                  uiSchema={EventPlanningUiSchema}
                  initialEditMode={true}
                  showEditButton={false}
                  showButtons={false}
                  editorOnly
                  onFormChange={handlePlanningChange}
                  customValidate={customPlanningValidator}
                />
              </div>
              {!isTask && (
                <>
                  {shouldShowAttributes && (
                    <>
                      <div className="event-modal__group-header">Type specific</div>
                      {attributesForm}
                    </>
                  )}
                  {!config.onPremise && (
                    <>
                      <div className="event-modal__group-header">Attachments</div>
                      {attachmentsField}
                    </>
                  )}
                </>
              )}
            </div>

            {isTask && (
              <div className="event-modal__column">
                {(modalState === FORM_STATES.view ||
                  !eventData.planning.recurrenceRule ||
                  eventData.planning.recurrenceRule === 'no-repeat') && (
                  <>
                    <div className="event-modal__group-header">Execution</div>
                    <div className="execution">
                      <JsonEditor
                        formRef={executionReference}
                        schema={executionSchema}
                        formData={eventData.execution}
                        uiSchema={EventExecutionUiSchema}
                        initialEditMode={true}
                        showEditButton={false}
                        showButtons={false}
                        editorOnly
                        onFormChange={({ formData }) =>
                          setEventData((data) => ({ ...data, execution: formData }))
                        }
                        customValidate={customExecutionValidator}
                      />
                    </div>
                  </>
                )}
                {shouldShowAttributes && (
                  <>
                    <div className="event-modal__group-header">Type specific</div>
                    {attributesForm}
                  </>
                )}
                {!config.onPremise && (
                  <>
                    <div className="event-modal__group-header">Attachments</div>
                    {attachmentsField}
                  </>
                )}
              </div>
            )}
          </div>

          {modalState !== FORM_STATES.view && (
            <div className="event-modal__actions">
              <Button
                activity="secondary"
                type="text"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                activity="primary"
                type="submit"
                onClick={onSubmitEvent}
                disabled={isSaving}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </SimpleModal>
      {deleteEventConfirmation}
    </>
  );
};

export default UserEventModal;
